import { Link, useParams } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/meta";
import { ArticleRow } from "@/components/feature/ArticleCards";

export default function ArticlePage() {
  const { slug = "" } = useParams();
  const a = articles.find((x) => x.slug === slug);
  const cat = categories.find((c) => c.slug === a?.category);

  useSeo(
    a ? `吃瓜精选盘点｜${a.title}` : "内容不存在",
    a ? `${a.excerpt}【吃瓜精选】` : "该内容不存在。"
  );

  if (!a || !cat) {
    return (
      <div className="wrap flex min-h-[60vh] flex-col items-center justify-center pt-16 text-center">
        <i className="ri-file-unknow-line text-5xl text-secondary-400" />
        <h1 className="serif mt-4 text-2xl font-bold text-foreground-950">找不到这篇内容</h1>
        <Link to="/" className="mt-5 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600">返回首页</Link>
      </div>
    );
  }

  const related = articles.filter((x) => x.category === a.category && x.slug !== a.slug).slice(0, 3);
  const fallback = related.length >= 3 ? related : related.concat(articles.filter((x) => x.slug !== a.slug && !related.includes(x)).slice(0, 3 - related.length));

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: a.title,
    description: a.excerpt,
    datePublished: `${a.date}T09:00:00+08:00`,
    author: { "@type": "Organization", name: "吃瓜精选" },
    publisher: { "@type": "Organization", name: "吃瓜精选" },
    articleSection: cat.name,
    keywords: a.tags.join(","),
  };

  return (
    <article className="pt-16 md:pt-[72px]">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* header */}
      <header className="border-b border-background-200/70 bg-background-100/50">
        <div className="wrap py-10 md:py-14">
          <nav className="flex flex-wrap items-center gap-1.5 text-sm text-secondary-600" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/category/${cat.slug}`} className="hover:text-primary-700">{cat.name}盘点</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-700 line-clamp-1">{a.title}</span>
          </nav>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <Link to={`/category/${cat.slug}`} className="rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700 hover:bg-primary-200">{cat.name}</Link>
            {a.tags.map((t) => (
              <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">{t}</span>
            ))}
            {a.hot && <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-foreground-950"><i className="ri-fire-fill" /> 热议中</span>}
          </div>

          <h1 className="serif mt-4 max-w-3xl text-2xl font-black leading-snug tracking-tight text-foreground-950 md:text-[2.3rem] md:leading-[1.2]">{a.title}</h1>
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-foreground-600">{a.excerpt}</p>

          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-secondary-600">
            <span className="inline-flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-background-50"><i className="ri-pen-nib-line text-sm" /></span>
              <span className="font-semibold text-foreground-800">{a.author}</span>
            </span>
            <span className="inline-flex items-center gap-1.5"><i className="ri-calendar-line" />发布于 {a.date}</span>
            <span className="inline-flex items-center gap-1.5"><i className="ri-time-line" />{a.read}阅读</span>
            <span className="inline-flex items-center gap-1.5"><i className="ri-eye-line" />{a.views}次围观</span>
          </div>
        </div>
      </header>

      {/* cover */}
      <div className="wrap mt-0">
        <div className="relative -mt-1">
          <div className="aspect-[16/8] w-full overflow-hidden rounded-b-2xl bg-background-200 md:aspect-[21/9]">
            <img src={a.cover} alt={`${a.title}封面图`} className="h-full w-full object-cover object-center" />
          </div>
        </div>
      </div>

      {/* body */}
      <div className="wrap py-10 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="max-w-content space-y-6 text-[15px] leading-[1.95] text-foreground-800 md:text-base">
              {a.content.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
              <span className="mr-1 text-sm font-semibold text-foreground-950">标签：</span>
              {a.tags.map((t) => (
                <Link key={t} to={`/category/${cat.slug}`} className="rounded-full border border-background-200 bg-background-50 px-3 py-1.5 text-xs text-secondary-700 transition-colors hover:border-primary-300 hover:text-primary-700">{t}</Link>
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-4 rounded-2xl border border-background-200/70 bg-background-100/50 p-5 sm:flex-row sm:items-center">
              <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary-500 text-background-50"><i className="ri-pen-nib-line text-xl" /></span>
              <div>
                <p className="text-sm font-bold text-foreground-950">关于这份盘点 · 吃瓜精选编辑部</p>
                <p className="mt-1 text-sm leading-relaxed text-secondary-700">我们坚持多信源、拉时间线、按章节呈现，力求把碎片信息整理成清晰、可读的完整脉络。如有补充或更正，欢迎来信告诉我们。</p>
              </div>
            </div>
          </div>

          {/* sidebar related */}
          <aside className="lg:col-span-4" aria-label="相关推荐">
            <div className="lg:sticky lg:top-24">
              <div className="flex items-center justify-between">
                <h2 className="serif text-lg font-bold text-foreground-950">相关推荐</h2>
                <span className="text-xs text-secondary-500">{cat.name}</span>
              </div>
              <div className="mt-4 space-y-3">
                {fallback.map((r, i) => (
                  <ArticleRow key={r.slug} a={r} index={i} />
                ))}
              </div>
              <Link to="/#collections" className="mt-4 flex items-center justify-between rounded-xl border border-primary-200 bg-primary-100/50 px-4 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-200/60">
                <span><i className="ri-flashlight-fill mr-1.5" />想看完整故事？</span>
                <span className="inline-flex items-center gap-1">去专题合集 <i className="ri-arrow-right-line" /></span>
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </article>
  );
}